import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Container, 
  Row, 
  Col, 
  Button, 
  Badge, 
  Spinner, 
  Modal, 
  Form, 
  Alert,
  Table,
  Pagination,
  Image
} from 'react-bootstrap';
import { 
  FaCar, 
  FaCheck, 
  FaTimes, 
  FaCalendarPlus, 
  FaEye,
  FaClock,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaSearch,
  FaFilter
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';

export default function AdminDrivers() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user) {
      fetchDrivers();
    }
  }, [user, currentPage, statusFilter]);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDrivers(currentPage, 10, statusFilter);
      if (response.success) {
        setDrivers(response.data);
        // Assuming the API returns pagination info
        setTotalPages(Math.ceil(response.data.length / 10));
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setMessage({ type: 'danger', text: 'Failed to fetch drivers' });
    } finally {
      setLoading(false);
    }
  };



  const handleStatusUpdate = async (driverId, newStatus) => {
    try {
      const response = await apiService.updateDriverStatus(driverId, newStatus);
      if (response.success) {
        setMessage({ type: 'success', text: `Driver status updated to ${newStatus}` });
        fetchDrivers(); // Refresh the list
      }
    } catch (error) {
      console.error('Error updating driver status:', error);
      setMessage({ type: 'danger', text: 'Failed to update driver status' });
    }
  };



  const getStatusVariant = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'inactive':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getStatusBadge = (status) => {
    const statusText = status.charAt(0).toUpperCase() + status.slice(1);
    return <Badge bg={getStatusVariant(status)}>{statusText}</Badge>;
  };

  const filteredDrivers = drivers.filter(driver =>
    driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    driver.car_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: "#f8fffe" }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="mt-3 text-muted">Loading drivers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: "#f8fffe" }}>
      <Container className="py-5">
        {/* Header */}
        <div className="mb-5">
          <div className="p-4 rounded-4 text-white position-relative overflow-hidden"
               style={{ background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)" }}>
            <Row className="align-items-center">
              <Col md={8}>
                <h1 className="display-6 fw-bold mb-3">Driver Management</h1>
                <p className="fs-5 opacity-90 mb-0">Manage driver registrations and approvals</p>
              </Col>
              <Col md={4} className="text-center">
                <div className="rounded-circle mx-auto d-flex align-items-center justify-content-center"
                     style={{
                       width: "80px",
                       height: "80px",
                       background: "rgba(255, 255, 255, 0.2)",
                       backdropFilter: "blur(10px)"
                     }}>
                  <FaCar size={32} />
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* Alert Messages */}
        {message.text && (
          <Alert variant={message.type} dismissible onClose={() => setMessage({ type: '', text: '' })}>
            {message.text}
          </Alert>
        )}

        {/* Filters and Search */}
        <Card className="border-0 rounded-4 mb-4" style={{ backgroundColor: "#f0fdfa" }}>
          <Card.Body className="p-4">
            <Row className="g-3">
              <Col md={4}>
                <div className="position-relative">
                  <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                  <Form.Control
                    type="text"
                    placeholder="Search drivers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="ps-5"
                  />
                </div>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Col>
              <Col md={5} className="text-end">
                <Badge bg="info" className="me-2 p-2">
                  Total Drivers: {filteredDrivers.length}
                </Badge>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Drivers Table */}
        <Card className="border-0 rounded-4" style={{ backgroundColor: "#f0fdfa" }}>
          <Card.Body className="p-0">
            <Table responsive className="mb-0">
              <thead style={{ backgroundColor: "#e0f2f1" }}>
                <tr>
                  <th className="border-0 p-3">Driver</th>
                  <th className="border-0 p-3">Vehicle Details</th>
                  {/* <th className="border-0 p-3">Status</th> */}
                  <th className="border-0 p-3">Phone</th>
                  <th className="border-0 p-3">Registration Date</th>
                  {/* <th className="border-0 p-3">Actions</th> */}
                </tr>
              </thead>
              <tbody>
                {filteredDrivers.length > 0 ? (
                  filteredDrivers.map((driver) => (
                    <tr key={driver.id} className="border-bottom">
                      <td className="p-3">
                        <div className="d-flex align-items-center">
                          {driver.images && driver.images.length > 0 ? (
                            <Image
                              src={driver.images[0]}
                              alt={driver.name}
                              className="rounded-circle me-3"
                              style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                            />
                          ) : (
                            <div className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                                 style={{
                                   width: '50px',
                                   height: '50px',
                                   backgroundColor: '#e0f2f1'
                                 }}>
                              <FaUser className="text-muted" />
                            </div>
                          )}
                          <div>
                            <h6 className="fw-bold mb-1">{driver.name}</h6>
                            <small className="text-muted">ID: {driver.id}</small>
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="fw-bold mb-1">{driver.car_name}</p>
                          <p className="text-muted mb-1">{driver.engine_capacity} • {driver.car_model_year}</p>
                        </div>
                      </td>
                      {/* <td className="p-3">
                        {getStatusBadge(driver.status)}
                      </td> */}
                      <td className="p-3">
                        <small className="text-muted">
                          {driver.phone_number}
                        </small>
                      </td>
                      <td className="p-3">
                        <small className="text-muted">
                          {new Date(driver.created_at).toLocaleDateString()}
                        </small>
                      </td>
                      {/* <td className="p-3">
                        <div className="d-flex gap-2">
                          {driver.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => handleStatusUpdate(driver.id, 'active')}
                                className="px-3"
                              >
                                <FaCheck className="me-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleStatusUpdate(driver.id, 'inactive')}
                                className="px-3"
                              >
                                <FaTimes className="me-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="primary"
                            // onClick={() => openAppointmentModal(driver)}
                            className="px-3"
                          >
                            <FaCalendarPlus className="me-1" />
                            Schedule
                          </Button>
                        </div>
                      </td> */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-5">
                      <FaCar size={48} className="text-muted mb-3" />
                      <h6 className="text-muted">No drivers found</h6>
                      <p className="text-muted">No drivers match your current filters</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4">
            <Pagination>
              <Pagination.Prev
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              />
              {[...Array(totalPages)].map((_, index) => (
                <Pagination.Item
                  key={index + 1}
                  active={currentPage === index + 1}
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        )}
      </Container>


    </div>
  );
}
